import React, { PropsWithChildren } from 'react';

import { Grid2 as Grid, styled } from '@mui/material';

import { ResponsiveGrid } from '@mewa/ui';

import {
  StrapiImage,
  StrapiResponsiveImageData,
} from '../../dynamic-content-page/content-components/image';

export const ArticleStage = React.memo(
  ({
    title,
    image,
    children,
  }: PropsWithChildren<{
    title: string;
    image: StrapiResponsiveImageData;
  }>) => {
    return (
      <Container>
        <PositionContainer>
          <ImageContainer>
            <StrapiImage
              responsiveImage={image}
              alt={title}
              intendedForSearchIndex
            />
            <GradientContainer />
          </ImageContainer>
        </PositionContainer>
        <TextContainer>
          <ResponsiveGrid>
            <Grid size={12}>
              <TitleContainer>
                <Title>{title}</Title>
              </TitleContainer>
            </Grid>
          </ResponsiveGrid>
          <ResponsiveGrid>
            <Grid
              size={{
                xs: 12,
                sm: 5,
                md: 4,
                lg: 4,
              }}
            >
              {children}
            </Grid>
          </ResponsiveGrid>
        </TextContainer>
      </Container>
    );
  },
);

const Container = styled('div')(({ theme }) => ({
  aspectRatio: 375 / 225,

  [theme.breakpoints.up('sm')]: {
    position: 'relative',
    aspectRatio: 900 / 350,
    overflow: 'hidden',
  },

  [theme.breakpoints.up('md')]: {
    aspectRatio: 1440 / 450,
  },

  [theme.breakpoints.up('lg')]: {
    aspectRatio: 1920 / 550,
  },
}));

const PositionContainer = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('sm')]: {
    position: 'absolute',
    inset: '0 0 0 0',
    zIndex: -1,
  },
}));

const ImageContainer = styled('div')(({ theme }) => ({
  img: {
    width: '100%',
    objectPosition: 'top',
  },

  position: 'relative',
  height: '100%',

  [theme.breakpoints.down('sm')]: {
    aspectRatio: '375 / 225',
    overflow: 'hidden',
  },

  '.gatsby-image-wrapper': {
    height: '100%',
    width: '100%',
  },
}));

const GradientContainer = styled('div')(({ theme }) => ({
  background:
    'linear-gradient(270deg, rgba(0, 0, 0, 0.00) 0%, rgba(0, 0, 0, 0.80) 100%)',
  inset: '0 50% 0 0',
  position: 'absolute',

  [theme.breakpoints.down('sm')]: {
    display: 'none',
  },
}));

const TextContainer = styled('div')(({ theme }) => ({
  paddingTop: '30px',
  display: 'flex',

  [theme.breakpoints.up('sm')]: {
    backgroundColor: 'unset',
    color: 'var(--color-white)',
    paddingTop: '40px',
  },

  [theme.breakpoints.up('md')]: {
    paddingTop: '70px',
  },

  [theme.breakpoints.up('lg')]: {
    paddingTop: '90px',
  },
}));

const TitleContainer = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('mdSm')]: {
    width: '55%',
  },
}));

const Title = styled('h1')(({ theme }) => ({
  ...theme.typography.h1,
  fontWeight: theme.typography.fontWeightLight,
  margin: 0,
}));
